// v3.31.6 · Diálogo de Permisos de Usuario — extraído de Users.js para bajar
// el archivo principal de 1251 → ~800 líneas.
// Props: toda la lógica (handlers + estado) sigue viviendo en Users.js; este
// componente es puramente presentacional + binding de controles.

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { PERMISSION_GROUPS, GROUP_COLORS, PERMISSION_MODULES } from '../../lib/permissionsConfig';

export function PermissionsDialog({
  open,
  onOpenChange,
  editingUser,
  editingPermissions,
  users,
  copyFromId,
  openPermGroups,
  setOpenPermGroups,
  getRoleLabel,
  onReset,
  onCopyFrom,
  onSave,
  onCancel,
  onPermissionChange,
  onGroupToggle,
  groupPermissionState,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurar Permisos</DialogTitle>
          <DialogDescription>
            {editingUser?.name} ({getRoleLabel(editingUser?.role)})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg space-y-3">
            <div className="flex justify-between items-center gap-3">
              <p className="text-sm text-blue-800">
                Personaliza los permisos de este usuario. Los cambios sobrescribirán los permisos por defecto del rol.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={onReset}
                className="shrink-0"
                data-testid="reset-permissions-btn"
              >
                Restablecer
              </Button>
            </div>
            <div className="flex items-center gap-2 border-t border-blue-200 pt-3">
              <Label className="text-sm text-blue-800 whitespace-nowrap">Copiar permisos de:</Label>
              <select
                data-testid="copy-permissions-select"
                value={copyFromId}
                onChange={(e) => onCopyFrom(e.target.value)}
                className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">— Elegí un usuario para copiar su configuración —</option>
                {users
                  .filter(u => u.user_id !== editingUser?.user_id && u.is_active)
                  .map(u => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name} · {getRoleLabel(u.role)}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="space-y-3">
            {(() => {
              const moduleByKey = Object.fromEntries(PERMISSION_MODULES.map(m => [m.key, m]));
              return PERMISSION_GROUPS.map((group) => {
                const groupModules = group.keys.map(k => moduleByKey[k]).filter(Boolean);
                if (groupModules.length === 0) return null;
                const isOpen = openPermGroups[group.id] !== false; // por defecto abierto
                const c = GROUP_COLORS[group.color] || GROUP_COLORS.slate;
                const GroupIcon = group.icon;
                const s = groupPermissionState(group);
                return (
                  <div key={group.id} className={`rounded-xl border ${c.border} overflow-hidden`}>
                    <div className={`w-full flex items-center justify-between px-4 py-3 ${c.bg}`}>
                      <button
                        type="button"
                        onClick={() => setOpenPermGroups({ ...openPermGroups, [group.id]: !isOpen })}
                        className="flex items-center gap-3 flex-1 text-left hover:brightness-105 transition"
                        data-testid={`perm-group-${group.id}`}
                      >
                        <span className={`flex items-center justify-center w-8 h-8 rounded-lg ring-1 ${c.ring} bg-white/70 dark:bg-black/30`}>
                          <GroupIcon className={`w-4 h-4 ${c.icon}`} />
                        </span>
                        <span className={`text-sm font-bold uppercase tracking-wide ${c.text}`}>
                          {group.label}
                        </span>
                        <span className={`text-xs ${c.text} opacity-70`}>
                          ({s.on}/{s.total})
                        </span>
                      </button>
                      <div className="flex items-center gap-3">
                        <label
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-2 cursor-pointer select-none"
                          title={s.all ? 'Desactivar todos los permisos de este grupo'
                                       : 'Activar todos los permisos de este grupo'}
                        >
                          <span className={`text-[11px] font-semibold uppercase ${c.text}`}>
                            {s.all ? 'Todo ON' : (s.on === 0 ? 'Todo OFF' : 'Parcial')}
                          </span>
                          <Switch
                            checked={s.all}
                            onCheckedChange={(v) => onGroupToggle(group, v)}
                            data-testid={`perm-group-toggle-${group.id}`}
                          />
                        </label>
                        <span className={`text-xs ${c.text}`}>{isOpen ? '▼' : '▶'}</span>
                      </div>
                    </div>
                    {isOpen && (
                      <div className="p-4 space-y-3 bg-background">
                        {groupModules.map((module) => (
                          <div key={module.key} className={`pl-3 border-l-2 ${c.border}`}>
                            <h4 className="font-semibold mb-2 text-sm flex items-center gap-2">
                              <GroupIcon className={`w-3.5 h-3.5 ${c.icon}`} />
                              {module.label}
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                              {module.permissions.map((perm) => (
                                <div key={perm.key} className="flex items-center space-x-2">
                                  <Switch
                                    id={`${module.key}-${perm.key}`}
                                    checked={editingPermissions[module.key]?.[perm.key] || false}
                                    onCheckedChange={(checked) => onPermissionChange(module.key, perm.key, checked)}
                                  />
                                  <Label htmlFor={`${module.key}-${perm.key}`} className="text-sm cursor-pointer">
                                    {perm.label}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onSave}>Guardar Permisos</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
